import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Booking, Vehicle } from "@/types";

export function useBooking(id: number | null) {
  const [booking, setBooking] = useState<Booking | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false);
      setError("We couldn't find that booking.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: bookingError } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (bookingError) throw bookingError;
      if (!data) {
        setBooking(null);
        setError("This booking could not be found.");
        return;
      }
      const found = data as Booking;
      setBooking(found);

      if (found.vehicle_id) {
        const { data: vehicleData } = await supabase
          .from("vehicles")
          .select("*")
          .eq("id", found.vehicle_id)
          .maybeSingle();
        setVehicle((vehicleData as Vehicle) ?? null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this booking right now.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { booking, vehicle, loading, error, reload: load };
}